import { Box, Typography, webPalette, webFonts } from '@munifocus/ui';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BrandMark } from './BrandMark';
import { Wrap } from './Wrap';
import { SITE_NAV } from './SiteHeader';
import { useContactDialog } from './ContactDialog';
import { BulletinSubscribe } from './BulletinSubscribe';
import { useCookieConsent } from '../providers/CookieConsentProvider';
import { publicReportUrl } from '../lib/portal-urls';

const LEGAL_LINKS = [
  { key: 'terms', to: '/terms' },
  { key: 'privacy', to: '/privacy' },
  { key: 'cookies', to: '/cookie-policy' },
] as const;

// Split the nav into two labeled footer columns so each has its own heading
// (rather than one heading orphaning a second, unlabeled column).
const EXPLORE_NAV = SITE_NAV.filter((n) => ['home', 'bulletin', 'training'].includes(n.key));
const COMPANY_NAV = SITE_NAV.filter((n) => ['services', 'about'].includes(n.key));

/** Ids linking each footer column's heading to its `<nav>` landmark. */
const EXPLORE_HEADING_ID = 'footer-explore-heading';
const COMPANY_HEADING_ID = 'footer-company-heading';

const footerHeadingSx = {
  fontSize: 12,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: webPalette.teal,
  // These render as `h2` now, so neutralise the UA heading margin explicitly —
  // the visual result must be identical to the div they replaced.
  mt: 0,
  mb: 1.75,
  fontWeight: 600,
} as const;

const footerLinkSx = {
  display: 'block',
  color: webPalette.onDarkSubtle,
  fontSize: 15,
  py: 0.625,
  '&:hover': { color: webPalette.onDark },
} as const;

export function SiteFooter() {
  const { t } = useTranslation();
  const { open } = useContactDialog();
  const { openPreferences } = useCookieConsent();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: webPalette.green,
        color: webPalette.onDark,
        pt: 8,
        pb: 5,
      }}
    >
      <Wrap>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: '1.6fr repeat(3, 1fr)' },
            columnGap: { xs: 4, md: 6 },
            rowGap: 5,
            alignItems: 'start',
            pb: 6,
            borderBottom: `1px solid ${webPalette.borderOnDarkSoft}`,
          }}
        >
          <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / -1', md: 'auto' } }}>
            <RouterLink to="/" style={{ display: 'inline-flex' }}>
              <BrandMark />
            </RouterLink>
            <Typography
              sx={{
                fontFamily: webFonts.bulletin,
                fontStyle: 'italic',
                fontSize: 22,
                color: webPalette.onDarkSoft,
                maxWidth: '20ch',
                mt: 2,
              }}
            >
              {t('web.footer.tagline')}
            </Typography>
          </Box>
          {/*
            The column captions were styled divs, so the footer had no headings
            and the link columns were not navigation landmarks — a screen-reader
            user had no way to jump between them (WCAG 1.3.1 / 2.4.1).
          */}
          <Box component="nav" aria-labelledby={EXPLORE_HEADING_ID}>
            <Box component="h2" id={EXPLORE_HEADING_ID} sx={footerHeadingSx}>
              {t('web.footer.explore')}
            </Box>
            {EXPLORE_NAV.map((n) => (
              <Box key={n.key} component={RouterLink} to={n.to} sx={footerLinkSx}>
                {t(`web.nav.${n.key}`)}
              </Box>
            ))}
          </Box>
          <Box component="nav" aria-labelledby={COMPANY_HEADING_ID}>
            <Box component="h2" id={COMPANY_HEADING_ID} sx={footerHeadingSx}>
              {t('web.footer.company')}
            </Box>
            {COMPANY_NAV.map((n) => (
              <Box key={n.key} component={RouterLink} to={n.to} sx={footerLinkSx}>
                {t(`web.nav.${n.key}`)}
              </Box>
            ))}
          </Box>
          <Box>
            <Box component="h2" sx={footerHeadingSx}>
              {t('web.footer.contact')}
            </Box>
            <Box
              component="a"
              href="mailto:info@munifocus.com"
              sx={{
                display: 'block',
                color: webPalette.onDarkSubtle,
                fontSize: 15,
                py: 0.625,
                '&:hover': { color: webPalette.onDark },
              }}
            >
              info@munifocus.com
            </Box>
            <Box
              sx={{
                display: 'block',
                color: webPalette.onDarkSubtle,
                fontSize: 15,
                py: 0.625,
              }}
            >
              {t('web.footer.location')}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => open()}
              sx={{
                display: 'block',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                textAlign: 'left',
                fontFamily: 'inherit',
                color: webPalette.onDarkSubtle,
                fontSize: 15,
                py: 0.625,
                '&:hover': { color: webPalette.onDark },
              }}
            >
              {t('web.footer.getInTouch')}
            </Box>
            {/* A full navigation to the Public Portal, not a router link: it is
                another app on its own origin. */}
            <Box component="a" href={publicReportUrl} sx={footerLinkSx}>
              {t('web.footer.reportProperty')}
            </Box>
          </Box>
        </Box>
        <Box sx={{ py: 5, borderBottom: `1px solid ${webPalette.borderOnDarkSoft}` }}>
          <BulletinSubscribe />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            mt: 4,
            fontSize: 13,
            color: webPalette.onDarkDim,
          }}
        >
          <Box component="span">{t('web.footer.rights', { year: new Date().getFullYear() })}</Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: { xs: 1.5, sm: 2.5 },
            }}
          >
            {LEGAL_LINKS.map((l) => (
              <Box
                key={l.key}
                component={RouterLink}
                to={l.to}
                sx={{
                  color: webPalette.onDarkDim,
                  '&:hover': { color: webPalette.onDark },
                }}
              >
                {t(`web.legal.${l.key}.navLabel`)}
              </Box>
            ))}
            <Box
              component="button"
              type="button"
              onClick={() => openPreferences()}
              sx={{
                background: 'none',
                border: 'none',
                p: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 13,
                color: webPalette.onDarkDim,
                '&:hover': { color: webPalette.onDark },
              }}
            >
              {t('web.cookies.managePreferences')}
            </Box>
          </Box>
        </Box>
      </Wrap>
    </Box>
  );
}
